import { useEffect, useMemo, useRef } from 'react';

export const useDebouncedSave = <T>(
  persist: (value: T) => unknown,
  ms: number
): {
  save: (value: T) => void;
  saveNow: (value: T) => unknown;
} => {
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const pendingRef = useRef<{ value: T } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const api = useMemo(() => {
    const clear = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const flush = () => {
      clear();
      if (pendingRef.current) {
        const { value } = pendingRef.current;
        pendingRef.current = null;
        persistRef.current(value);
      }
    };
    return {
      save: (value: T) => {
        pendingRef.current = { value };
        clear();
        timeoutRef.current = setTimeout(flush, ms);
      },
      saveNow: (value: T) => {
        pendingRef.current = null;
        clear();
        return persistRef.current(value);
      },
      flush,
    };
  }, [ms]);

  useEffect(() => () => api.flush(), [api]);

  return api;
};
